<template>
  <v-dialog v-model="dialog" persistent scrollable max-width="604px">
    <template #activator="{ on }">
      <app-button v-on="on" data-e2e-id="manage-users-invite-btn">Invite</app-button>
    </template>
    <v-card flat class="pb-3 px-2">
      <v-card-title class="pb-0">
        <h6 class="title">Invite User</h6>
      </v-card-title>
      <v-card-text class="card-text-height">
        <v-row>
          <v-col>
            <v-row>
              <v-col>
                <label class="font-weight-medium">First name *</label>
                <v-text-field
                  data-firstname
                  v-model.lazy.trim="form.firstName"
                  background-color="light"
                  dense
                  :error-messages="firstNameErrors"
                  filled
                  :hide-details="firstNameErrors.length === 0"
                  label="Jane"
                  single-line
                  outlined
                  data-e2e-id="invite-user-first-name-txtbox"
                  @change="$v.form.firstName.$touch()"
                />
              </v-col>
              <v-col>
                <label class="font-weight-medium">Last name *</label>
                <v-text-field
                  data-lastname
                  v-model.lazy.trim="form.lastName"
                  background-color="light"
                  dense
                  :error-messages="lastNameErrors"
                  filled
                  :hide-details="lastNameErrors.length === 0"
                  label="Doe"
                  outlined
                  single-line
                  data-e2e-id="invite-user-last-name-txtbox"
                  @change="$v.form.lastName.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <label class="font-weight-medium">Email *</label>
                <v-text-field
                  data-email
                  v-model.lazy.trim="form.email"
                  background-color="light"
                  dense
                  :error-messages="emailErrors"
                  filled
                  :hide-details="emailErrors.length === 0"
                  label="jane.doe@gmail.com"
                  outlined
                  single-line
                  data-e2e-id="invite-user-email-txtbox"
                  @change="$v.form.email.$touch()"
                />
              </v-col>
            </v-row>
            <v-row>
              <v-col>
                <h3>Roles</h3>
              </v-col>
            </v-row>
            <v-row class="flex-column">
              <v-col v-for="role in roleOptions" :key="role.value" class="striped">
                <v-row class="py-2">
                  <v-col class="py-0">
                    {{ role.text }}
                    <app-tooltip v-if="getDescriptionForRole(role.value).length > 0" right>
                      <template #activator="{ on }">
                        <sup
                          ><v-icon small class="material-icons-round" v-on="on">info</v-icon></sup
                        >
                      </template>
                      <div
                        v-for="(descriptionLine, index) in getDescriptionForRole(role.value)"
                        :key="index"
                      >
                        <span>{{ descriptionLine }}</span>
                        <br />
                      </div>
                    </app-tooltip>
                  </v-col>
                  <v-col cols="1" class="py-0">
                    <v-checkbox
                      v-model="form.roles"
                      :value="role.value"
                      color="primary"
                      dense
                      hide-details
                      :data-e2e-id="`invite-user-role-${kebab(role.text)}`"
                    />
                  </v-col>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
      <v-card-actions class="pb-3 pr-8">
        <v-spacer />
        <app-button-cancel :disabled="saving" @click="clearForm()" />
        <app-button data-send :disabled="$v.$invalid" :request="submitForm" data-e2e-id="invite-user-send-btn">Send</app-button>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { email, required } from 'vuelidate/lib/validators'
import { userService } from '@/services/user-service'
import { membershipService } from '@/services/membership-service'
import { roleService } from '@/services/role-service'
import { getDescriptionForRole } from '@/helpers/role-helper'
import { MyActiveOrganization } from '@/helpers/my-account-helper'
import { permissionsMixin } from '@/mixins/permissions'
import _ from 'lodash'

export default {
  mixins: [validationMixin],
  props: {
    users: { type: Object, required: true }
  },
  data() {
    return {
      dialog: false,
      form: {
        email: '',
        firstName: '',
        lastName: '',
        roles: []
      },
      roles: [],
      saving: false
    }
  },
  computed: {
    emailErrors() {
      const errors = []
      if (!this.$v.form.email.$dirty) return errors
      !this.$v.form.email.required && errors.push('Email is required')
      !this.$v.form.email.email && errors.push('Email incorrectly formatted')
      !this.$v.form.email.new && errors.push('User already invited')
      return errors
    },
    firstNameErrors() {
      const errors = []
      if (!this.$v.form.firstName.$dirty) return errors
      !this.$v.form.firstName.required && errors.push('First name is required')
      return errors
    },
    lastNameErrors() {
      const errors = []
      if (!this.$v.form.lastName.$dirty) return errors
      !this.$v.form.lastName.required && errors.push('Last name is required')
      return errors
    },
    emails() {
      return _.map(_.mapValues(this.users, 'email'))
    },
    roleOptions() {
      return _.sortBy(
        _.map(this.permittedRoles, role => {
          return { text: _.startCase(role.name), value: role.id }
        }),
        'text'
      )
    },
    displayAdminRole() {
      return permissionsMixin.methods.hasAnyPermission([
        'MEMBERSHIP_ADMINISTRATOR_CREATE',
        'MEMBERSHIP_ADMINISTRATOR_DELETE'
      ])
    },
    displayRealTimeFeasibilityRole() {
      return permissionsMixin.methods.hasAnyPermission([
        'DEEP_LENS_COORDINATOR_CREATE',
        'DEEP_LENS_COORDINATOR_DELETE'
      ])
    },
    permittedRoles() {
      let validRoles = _.filter(this.roles, role => role.id !== 'HIDDEN')
      if (!this.displayAdminRole) {
        validRoles = _.reject(validRoles, role => {
          return role.id === 'ADMINISTRATOR'
        })
      }
      if (!this.displayRealTimeFeasibilityRole) {
        validRoles = _.reject(validRoles, role => {
          return role.id === 'DEEP_LENS_COORDINATOR'
        })
      }
      return validRoles
    }
  },
  async created() {
    this.roles = _.sortBy((await roleService.list()).data, 'name')
  },
  methods: {
    clearForm() {
      this.form.email = ''
      this.form.firstName = ''
      this.form.lastName = ''
      this.form.roles = []
      this.$v.$reset()
      this.saving = false
      this.dialog = false
    },
    async submitForm() {
      this.$v.$touch()
      if (!this.$v.$invalid) {
        this.saving = true
        await userService.create(MyActiveOrganization.id, {
          username: this.form.email.toLowerCase(),
          email: this.form.email.toLowerCase(),
          firstName: this.form.firstName,
          lastName: this.form.lastName
        })
        await membershipService.update(MyActiveOrganization.id, {
          user: {
            username: this.form.email.toLowerCase()
          },
          userRoles: _.map(this.form.roles, it => {
            return { id: it }
          })
        })
        this.$emit('user-invited', this.form.email.toLowerCase())
        this.clearForm()
      }
    },
    getDescriptionForRole(role) {
      return getDescriptionForRole(role)
    },
    kebab(name) {
      return _.kebabCase(name)
    }
  },
  validations: {
    form: {
      firstName: { required },
      lastName: { required },
      email: {
        required,
        email,
        new: function(value) {
          return !_.includes(this.emails, value.toLowerCase())
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
/* stylelint-disable-next-line selector-pseudo-element-no-unknown */
div ::v-deep .v-input--selection-controls {
  padding-top: 0;
  margin-top: 0 !important;
}

.striped.col:nth-of-type(odd) {
  background-color: var(--v-medium-lighten1);
}
</style>
